<script setup lang="ts">
import { Label } from '../label';
import { Input } from '../input';
import { Button } from '../button';
import type { Facility, FacilityGroupID, SimpleSelectable } from '../../cross-app-shared/types/db';
import FacilityStringListInput from './FacilityStringListInput.vue';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { alertModal } from '../modals/AlertModal.vue';
import { generateId } from '../../cross-app-shared/services/generate-id';
import { crossAppActions } from '../cross-app-actions';
import { facilitiesByFacilityID, facilitiesRefresh, groupsByGroupID } from '../facilities';
import { toast } from '../toast';
import { epaFacilityToAppFacility } from '../../cross-app-shared/services/epa-api';
import { Alert, AlertDescription, AlertTitle } from '../alert';
import { Card, CardContent, CardHeader, CardTitle } from '../card';
import { Progress } from '../progress';
import SelectTimeZone from '../SelectTimeZone.vue';
import type { AppID } from '../../tri-shared/types/db.ts';

const props = defineProps<{
  readonly: boolean;
  mode: 'create' | 'update';
  apps: AppID[];
}>();

const emit = defineEmits<{
  saved: [facility: SimpleSelectable<Facility>];
}>();

defineExpose({
  reportValidityForFormR: async (): Promise<boolean> => {
    enforceRequired.value = true;
    if (facilityEditing.value.naicsCodes.length === 0) {
      facilityEditing.value.naicsCodes.push('');
    }
    await nextTick();
    return form.value?.reportValidity() ?? true;
  },
});

const form = useTemplateRef('form');
const enforceRequired = ref(false);
const facilityEditing = defineModel<Omit<SimpleSelectable<Facility>, 'timeZone'>>({ required: true });

const saving = ref(false);
const saved = ref(false);

// The time zone the database falls back to (based on the facility's state) when none is configured. It is only known
// once the facility has been saved.
const timeZoneFallback = computed((): string | null => {
  if (props.mode === 'create' || facilityEditing.value.timeZoneConfigured !== null) {
    return null;
  }
  return facilitiesByFacilityID.value[facilityEditing.value.facilityID]?.timeZone ?? null;
});

watch(
  facilityEditing,
  useDebounceFn(async () => {
    if (props.mode === 'create') {
      // Creation mode does not save until the user initiates it.
    } else {
      await save();
    }
  }, 300),
  { deep: true },
);

async function save() {
  if (saving.value) {
    return;
  }

  try {
    saving.value = true;
    saved.value = false;
    let facilityUpdated: SimpleSelectable<Facility>;
    if (props.mode === 'create') {
      facilityUpdated = await crossAppActions.facilities.create(facilityEditing.value);
      toast({
        title: 'Facility created',
        description: `${facilityUpdated.name} is now in the system.`,
      });
    } else {
      facilityUpdated = await crossAppActions.facilities.detailsUpdate(facilityEditing.value);
    }

    saved.value = true;

    await facilitiesRefresh();

    emit('saved', facilityUpdated);
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to save facility',
      message: `An unexpected error occurred and we were unable to save your updates. Error was: ${e.message}`,
    });
  } finally {
    saving.value = false;
  }
}

const formId = generateId('form');

const groups = computed(() => {
  let groupSet: Map<FacilityGroupID, string>;
  if (props.mode === 'create') {
    groupSet = new Map<FacilityGroupID, string>(
      Object.values(groupsByGroupID.value).map((g) => [g.facilityGroupID, g.tenantName + ' - ' + g.name]),
    );
  } else {
    groupSet = new Map<FacilityGroupID, string>(
      Object.values(groupsByGroupID.value)
        .filter((g) => g.tenantID === facilityEditing.value.tenantID)
        .map((g) => [g.facilityGroupID, g.name]),
    );
  }

  return Array.from(groupSet.entries())
    .map(([facilityGroupID, groupName]) => ({ facilityGroupID, groupName }))
    .sort((a, b) => {
      return a.groupName.localeCompare(b.groupName);
    });
});

// Helper function to count completed fields
function isFieldComplete(value: string | string[] | null | undefined): boolean {
  if (Array.isArray(value)) {
    return value.length > 0 && value.some((v) => v.trim().length > 0);
  }
  return typeof value === 'string' && value.trim().length > 0;
}

// Completion counters for each section
const requiredFieldsCompletion = computed(() => {
  const requiredFields = [
    facilityEditing.value.triID,
    facilityEditing.value.name,
    facilityEditing.value.properName,
    facilityEditing.value.naicsCodes,
    facilityEditing.value.certifierName,
    facilityEditing.value.certifierTitle,
    facilityEditing.value.publicContactName,
    facilityEditing.value.publicContactEmail,
    facilityEditing.value.publicContactPhone,
    facilityEditing.value.technicalContactName,
    facilityEditing.value.technicalContactEmail,
    facilityEditing.value.technicalContactPhone,
  ];

  const completed = requiredFields.filter(isFieldComplete).length;
  return { completed, total: requiredFields.length };
});

const addressFieldsCompletion = computed(() => {
  const addressFields = [
    facilityEditing.value.address,
    facilityEditing.value.city,
    facilityEditing.value.county,
    facilityEditing.value.state,
    facilityEditing.value.zip,
  ];

  const completed = addressFields.filter(isFieldComplete).length;
  return { completed, total: addressFields.length };
});

const requiredIfAvailableCompletion = computed(() => {
  const fields = [
    facilityEditing.value.parentCompanyName,
    facilityEditing.value.parentCompanyDunBradstreetCode,
    facilityEditing.value.foreignParentCompanyName,
    facilityEditing.value.foreignParentCompanyDunBradstreetCode,
    facilityEditing.value.dunBradstreetCodes,
  ];

  const completed = fields.filter(isFieldComplete).length;
  return { completed, total: fields.length };
});

const optionalFieldsCompletion = computed(() => {
  const optionalFields = [
    facilityEditing.value.npdesPermitNumbers,
    facilityEditing.value.rcraIdNumbers,
    facilityEditing.value.uicIdNumbers,
  ];

  // facilityGroupID is always set, so add one to ensure the counts are accurate:
  const completed = optionalFields.filter(isFieldComplete).length + 1;
  return { completed, total: optionalFields.length + 1 };
});

// Overall completion tracking for required fields only
const overallCompletion = computed(() => {
  const required = requiredFieldsCompletion.value;
  const address = addressFieldsCompletion.value;

  const totalCompleted = required.completed + address.completed;
  const totalFields = required.total + address.total;

  return {
    completed: totalCompleted,
    total: totalFields,
    percentage: totalFields > 0 ? Math.round((totalCompleted / totalFields) * 100) : 0,
  };
});

const awaitingEpa = ref(false);
const epaFillSuccess = ref(false);
async function fillFromEpa() {
  if (!facilityEditing.value.triID) {
    return;
  }

  try {
    awaitingEpa.value = true;
    epaFillSuccess.value = false;
    const found = await epaFacilityToAppFacility(facilityEditing.value.triID);
    if (!found) {
      await alertModal({
        title: 'No facility data found',
        message: 'Is your TRI ID correct? If so, the facility may not have filed form R in recent years.',
      });
      return;
    }

    let updated = 0;
    for (const [key, value] of Object.entries(found)) {
      if (value === null || value.length === 0) {
        continue;
      }

      const existing = (facilityEditing.value as Record<string, unknown>)[key];
      if ((typeof existing === 'string' || Array.isArray(existing)) && existing.length > 0) {
        continue;
      }

      (facilityEditing.value as Record<string, unknown>)[key] = value;
      updated += 1;
    }

    toast({
      title: `Updated ${updated} field${updated === 1 ? '' : 's'}`,
    });
    epaFillSuccess.value = true;

    // Auto-save after EPA import if in update mode
    if (props.mode === 'update' && updated > 0) {
      await save();
    }
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to import info',
      message: 'An error occurred and we were unable to import the facility information from the EPA.',
    });
  } finally {
    awaitingEpa.value = false;
  }
}
</script>

<template>
  <div>
    <Card class="border-0 shadow-none">
      <CardHeader class="ps-1">
        <CardTitle class="flex flex-row items-center justify-between">
          <span>
            {{ mode === 'create' ? 'Create New Facility: ' : 'Update Facility: '
            }}{{ facilityEditing.name || 'Unnamed facility' }}
          </span>
          <div v-if="props.mode === 'create' && !readonly" class="flex justify-end gap-3">
            <Button type="submit" :disabled="saving" :form="formId">
              <template v-if="saving">
                <i class="fa fa-spin fa-spinner-third"></i>
                Creating Facility...
              </template>
              <template v-else>
                <i class="fa-light fa-plus"></i>
                Create Facility
              </template>
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
    </Card>

    <!-- Overall Progress Card -->
    <Card v-if="!readonly && overallCompletion.total > 0" class="mb-6">
      <CardHeader>
        <CardTitle>
          <div class="mb-2 flex items-center justify-between">
            <span class="text-lg font-semibold">Overall Completion Progress</span>
            <span
              class="text-lg font-bold"
              :class="overallCompletion.percentage === 100 ? 'text-green-600' : 'text-blue-600'"
            >
              {{ overallCompletion.percentage }}%
            </span>
          </div>

          <div class="space-y-3">
            <div class="relative">
              <Progress :modelValue="overallCompletion.percentage" class="h-3" />
            </div>
            <p class="mt-2 flex justify-between text-xs text-gray-500">
              <span
                >Progress shows required and address fields only. Conditional and optional fields are not
                included.</span
              >

              <span v-if="saving" class="ml-2 text-xs text-amber-600">
                <i class="fa-light fa-circle-dot text-xs"></i>
                Auto-saving...
              </span>
              <span v-else-if="saved" class="ml-2 text-xs text-green-600">
                <i class="fa-light fa-check"></i>
                Saved
              </span>
            </p>
          </div>
        </CardTitle>
      </CardHeader>
    </Card>

    <form :id="formId" @submit.prevent="save()" ref="form">
      <!-- TRI ID Card -->
      <Card class="mb-6">
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <i class="fa-solid fa-id-badge text-gray-600"></i>
            <span>TRI Facility ID*</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Label class="block">
            <div class="flex gap-2">
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.triID"
                placeholder="Enter your facility's TRI ID (e.g., 12345ABCDE123456)..."
                required
                class="flex-1"
                @keydown.enter.prevent="fillFromEpa()"
              />
              <Button
                v-if="!readonly"
                type="button"
                variant="outline"
                :disabled="saving || !facilityEditing.triID || awaitingEpa"
                @click.prevent="fillFromEpa()"
              >
                <template v-if="awaitingEpa">
                  <i class="fa fa-spin fa-spinner-third mr-2"></i>
                  <span class="hidden sm:inline">Importing...</span>
                  <span class="sm:hidden">...</span>
                </template>
                <template v-else>
                  <i class="fa-light fa-file-import mr-2"></i>
                  <span v-if="facilityEditing.triID" class="hidden sm:inline">Import from EPA</span>
                  <span v-if="facilityEditing.triID" class="sm:hidden">Import</span>
                  <span v-if="!facilityEditing.triID" class="hidden sm:inline">Enter ID first</span>
                  <span v-if="!facilityEditing.triID" class="sm:hidden">Need ID</span>
                </template>
              </Button>
            </div>
            <p v-if="!readonly" class="mt-2 text-sm text-gray-600">
              <i class="fa-light fa-info-circle mr-1"></i>
              Your TRI ID enables automatic import of facility details from EPA records.
              <span v-if="!facilityEditing.triID"
                >Enter your ID above, then click "Import from EPA" to auto-fill facility details.</span
              >
              <span v-else>Ready! Click "Import from EPA" to auto-fill facility details.</span>
            </p>
          </Label>
        </CardContent>
      </Card>

      <Alert v-if="epaFillSuccess" variant="info">
        <AlertTitle>Data updated from the EPA</AlertTitle>
        <AlertDescription>Fields still blank below were not included in the prior year's report.</AlertDescription>
      </Alert>

      <!-- Required Fields Card -->
      <Card class="mb-6">
        <CardHeader>
          <CardTitle class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <i class="fa-solid fa-asterisk text-sm text-red-500"></i>
              Required Information
            </span>
            <span
              class="rounded-full border px-2 py-1 text-sm font-semibold"
              :class="
                requiredFieldsCompletion.completed === requiredFieldsCompletion.total
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-blue-200 bg-blue-50 text-blue-700'
              "
            >
              {{ requiredFieldsCompletion.completed }}/{{ requiredFieldsCompletion.total }}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div class="grid grid-cols-2 gap-4">
            <Label>
              <span class="mb-2 block">Display Name*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.name"
                placeholder="Enter display name for internal use..."
                required
              />
              <p class="mt-1 text-xs text-gray-500">Internal name used in lists and navigation</p>
            </Label>
            <Label>
              <span class="mb-2 block">Official Facility Name*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.properName"
                placeholder="Enter official facility name for EPA forms..."
                required
              />
              <p class="mt-1 text-xs text-gray-500">Official name that appears on EPA Form R submissions</p>
            </Label>
            <Label>
              <span class="mb-2 block">NAICS Codes*</span>
              <FacilityStringListInput
                :readonly="readonly"
                v-model="facilityEditing.naicsCodes"
                placeholder="NAICS Code..."
              />
            </Label>
            <Label>
              <span class="mb-2 block">Certifying Official's Name*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.certifierName"
                placeholder="Enter certifying official's name..."
                :required="enforceRequired"
              />
            </Label>
            <Label>
              <span class="mb-2 block">Certifying Official's Title*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.certifierTitle"
                placeholder="Enter certifying official's title..."
                :required="enforceRequired"
              />
            </Label>
            <Label>
              <span class="mb-2 block">Public Contact Name*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.publicContactName"
                placeholder="Enter public contact's name..."
                :required="enforceRequired"
              />
            </Label>
            <Label>
              <span class="mb-2 block">Public Contact Email*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.publicContactEmail"
                placeholder="Enter public contact's email..."
                :required="enforceRequired"
              />
            </Label>
            <div class="flex gap-2">
              <Label class="flex-1">
                <span class="mb-2 block">Public Contact Phone*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.publicContactPhone"
                  placeholder="Phone number..."
                  :required="enforceRequired"
                />
              </Label>
              <Label class="flex-1">
                <span class="mb-2 block">Extension</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.publicContactPhoneExt"
                  placeholder="Ext..."
                />
              </Label>
            </div>
            <Label>
              <span class="mb-2 block">Technical Contact Name*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.technicalContactName"
                placeholder="Enter technical contact's name..."
                :required="enforceRequired"
              />
            </Label>
            <Label>
              <span class="mb-2 block">Technical Contact Email*</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.technicalContactEmail"
                placeholder="Enter technical contact's email..."
                :required="enforceRequired"
              />
            </Label>
            <div class="flex gap-2">
              <Label class="flex-1">
                <span class="mb-2 block">Technical Contact Phone*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.technicalContactPhone"
                  placeholder="Phone number..."
                  :required="enforceRequired"
                />
              </Label>
              <Label class="flex-1">
                <span class="mb-2 block">Extension</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.technicalContactPhoneExt"
                  placeholder="Ext..."
                />
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Address Information Card -->
      <Card class="mb-6">
        <CardHeader>
          <CardTitle class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <i class="fa-solid fa-map-location-dot text-sm text-blue-600"></i>
              Address Information
            </span>
            <div class="flex items-center gap-2">
              <span
                class="rounded-full border px-2 py-1 text-sm font-semibold"
                :class="
                  addressFieldsCompletion.completed === addressFieldsCompletion.total
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
                "
              >
                {{ addressFieldsCompletion.completed }}/{{ addressFieldsCompletion.total }}
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div class="flex gap-4">
            <div class="grid min-w-0 flex-1 grid-cols-2 gap-4 self-start">
              <Label class="col-span-2">
                <span class="mb-2 block">Address*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.address"
                  placeholder="Enter address of facility..."
                  :required="enforceRequired"
                />
              </Label>
              <Label>
                <span class="mb-2 block">City*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.city"
                  placeholder="Enter city..."
                  :required="enforceRequired"
                />
              </Label>
              <Label>
                <span class="mb-2 block">County*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.county"
                  placeholder="Enter county..."
                  :required="enforceRequired"
                />
              </Label>
              <Label>
                <span class="mb-2 block">State (2 letter)*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.state"
                  @blur="facilityEditing.state = facilityEditing.state.toUpperCase()"
                  maxlength="2"
                  placeholder="Enter state..."
                  :required="enforceRequired"
                />
              </Label>
              <Label>
                <span class="mb-2 block">ZIP Code*</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.zip"
                  placeholder="Enter ZIP code..."
                  :required="enforceRequired"
                />
              </Label>
              <Label class="col-span-2">
                <span class="mb-2 block">Mailing Address (if different)</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.mailingAddress"
                  placeholder="Enter mailing address..."
                />
              </Label>
              <Label>
                <span class="mb-2 block">Mailing City</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.mailingCity"
                  placeholder="Enter mailing city..."
                />
              </Label>
              <Label>
                <span class="mb-2 block">Mailing State (2 letter)</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.mailingState"
                  @blur="facilityEditing.mailingState = facilityEditing.mailingState.toUpperCase()"
                  placeholder="Enter mailing state..."
                  maxlength="2"
                />
              </Label>
              <Label>
                <span class="mb-2 block">Mailing ZIP Code</span>
                <Input
                  :disabled="readonly"
                  type="text"
                  v-model.trim="facilityEditing.mailingZip"
                  placeholder="Enter mailing ZIP code..."
                />
              </Label>
            </div>
            <slot name="address-map" />
          </div>
        </CardContent>
      </Card>

      <!-- Required if Available Card -->
      <Card class="mb-6">
        <CardHeader>
          <CardTitle class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <i class="fa-solid fa-circle-question text-sm text-yellow-600"></i>
              <span>
                Required if Available
                <span class="text-sm font-normal text-gray-600">(Complete if you have this information)</span>
              </span>
            </span>
            <div class="flex items-center gap-2">
              <span
                class="rounded-full border px-2 py-1 text-sm font-semibold"
                :class="
                  requiredIfAvailableCompletion.completed === requiredIfAvailableCompletion.total
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-yellow-200 bg-yellow-50 text-yellow-700'
                "
              >
                {{ requiredIfAvailableCompletion.completed }}/{{ requiredIfAvailableCompletion.total }}
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div class="grid grid-cols-2 gap-4">
            <Label>
              <span class="mb-2 block">Parent Company Name†</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.parentCompanyName"
                placeholder="Enter parent company name, if applicable..."
              />
            </Label>
            <Label>
              <span class="mb-2 block">Parent Company Dun &amp; Bradstreet Code†</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.parentCompanyDunBradstreetCode"
                placeholder="Enter parent company D&B code, if applicable..."
                maxlength="9"
              />
            </Label>
            <Label>
              <span class="mb-2 block">Foreign Parent Company Name†</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.foreignParentCompanyName"
                placeholder="Enter foreign parent company name, if applicable..."
              />
            </Label>
            <Label>
              <span class="mb-2 block">Foreign Parent Company Dun &amp; Bradstreet Code†</span>
              <Input
                :disabled="readonly"
                type="text"
                v-model.trim="facilityEditing.foreignParentCompanyDunBradstreetCode"
                placeholder="Enter foreign parent company D&B code, if applicable..."
                maxlength="9"
              />
            </Label>
            <Label>
              <span class="mb-2 block">Dun &amp; Bradstreet Code(s)†</span>
              <FacilityStringListInput
                :readonly="readonly || saving"
                v-model="facilityEditing.dunBradstreetCodes"
                placeholder="D&B Code..."
                :maxlength="9"
                pattern="^[0-9]{9}$"
              />
            </Label>
          </div>
        </CardContent>
      </Card>

      <!-- Optional Information Card -->
      <Card class="mb-6">
        <CardHeader>
          <CardTitle class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <i class="fa-solid fa-gear text-sm text-gray-500"></i>
              <span>
                Optional Information
                <span class="text-sm font-normal text-gray-600"
                  >(Helpful for organization and compliance tracking)</span
                >
              </span>
            </span>
            <div class="flex items-center gap-2">
              <span
                class="rounded-full border px-2 py-1 text-sm font-semibold"
                :class="
                  optionalFieldsCompletion.completed === optionalFieldsCompletion.total
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-gray-50 text-gray-700'
                "
              >
                {{ optionalFieldsCompletion.completed }}/{{ optionalFieldsCompletion.total }}
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div class="grid grid-cols-2 gap-4">
            <Label>
              <span class="mb-2 block">Facility Group</span>
              <select
                class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white file:border-0 file:bg-transparent placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="readonly"
                v-model="facilityEditing.facilityGroupID"
              >
                <option v-for="group of groups" :key="group.facilityGroupID" :value="group.facilityGroupID">
                  {{ group.groupName }}
                </option>
              </select>
            </Label>
            <Label>
              <span class="mb-2 block">NPDES Permit Numbers</span>
              <FacilityStringListInput
                :readonly="readonly || saving"
                v-model="facilityEditing.npdesPermitNumbers"
                placeholder="NPDES Permit Number..."
              />
            </Label>
            <Label>
              <span class="mb-2 block">RCRA ID Numbers</span>
              <FacilityStringListInput
                :readonly="readonly || saving"
                v-model="facilityEditing.rcraIdNumbers"
                placeholder="RCRA Number..."
              />
            </Label>
            <Label>
              <span class="mb-2 block">UIC ID Numbers</span>
              <FacilityStringListInput
                :readonly="readonly || saving"
                v-model="facilityEditing.uicIdNumbers"
                placeholder="UIC Number..."
              />
            </Label>
          </div>
        </CardContent>
      </Card>

      <Card v-if="apps.includes('compliance') || apps.includes('hazwaste')" class="mb-6">
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <i class="fa-solid fa-clock text-gray-500" />
            Compliance time zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p class="mb-3 text-sm text-gray-600">
            Controls due dates, recurrence boundaries, and notification timing for Compliance tasks at this facility.
          </p>
          <SelectTimeZone
            v-model="facilityEditing.timeZoneConfigured"
            :timeZoneFallback="timeZoneFallback"
            :disabled="readonly || saving"
          />
        </CardContent>
      </Card>

      <slot name="extra-sections" />

      <div v-if="props.mode === 'create' && !readonly" class="mt-6 flex justify-end gap-3">
        <Button type="submit" :disabled="saving">
          <template v-if="saving">
            <i class="fa fa-spin fa-spinner-third"></i>
            Creating Facility...
          </template>
          <template v-else>
            <i class="fa-light fa-plus"></i>
            Create Facility
          </template>
        </Button>
      </div>

      <p class="mt-6 text-sm text-gray-600">
        * = Required for Form R submission<br />
        † = Required for Form R submission if available
      </p>
    </form>
  </div>
</template>
